import {useTranslation} from 'react-i18next';
import React, {useState} from 'react';

import type {AriaMenuProps, ButtonProps} from '@pexip/components';
import {Button, Icon, IconTypes, Tooltip} from '@pexip/components';

import {TestId} from '../../../../test/testIds';

import {ToolbarAriaMenu} from './ToolbarAriaMenu.view';
import {getLangDirection} from '../../../utils/getLangDirection';

export const MoreMenu: React.FC<
    Omit<AriaMenuProps, 'button'> &
        Pick<ButtonProps, 'children' | 'size'> & {
            isTouch?: boolean;
            isMobile?: boolean;
        }
> = ({
    items,
    onOpenChange,
    isTouch = false,
    isMobile = false,
    children,
    ...props
}) => {
    const [isMoreMenuOpen, setMoreMenuOpen] = useState(false);

    const {t, i18n} = useTranslation();
    const dir = getLangDirection(i18n);

    const moreControlsText = t('meeting.more-controls', 'More controls');
    return (
        <ToolbarAriaMenu
            items={items}
            onOpenChange={isOpen => {
                setMoreMenuOpen(isOpen);
                onOpenChange?.(isOpen);
            }}
            button={
                <Button
                    isActive={isMoreMenuOpen}
                    aria-label={moreControlsText}
                    modifier="square"
                    data-testid={TestId.ButtonToolbarMoreItems}
                    variant={isTouch ? 'neutral' : 'translucent'}
                    size="large"
                >
                    <Icon
                        source={
                            isMobile
                                ? IconTypes.IconMoreHorizontal
                                : IconTypes.IconMoreVertical
                        }
                        size={isMobile ? 'medium' : undefined}
                    />
                    {children}
                </Button>
            }
            isTouch={isTouch}
            renderButton={triggerButton => (
                <Tooltip
                    text={moreControlsText}
                    role="tooltip"
                    position={
                        isTouch ? 'top' : dir === 'rtl' ? 'left' : 'right'
                    }
                >
                    {triggerButton}
                </Tooltip>
            )}
            {...props}
        />
    );
};
